import React, {FC, useState, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { Table, TableSkeleton, TopProgress, Typography, Button } from "../../../../../../components";
import {
  Container
} from './styles'
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { billingService, projectService } from "../../../../../../api";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import { icons } from "../../../../../../icons";
import { useUi } from "../../../../../../context/ui.context";
import Skeleton from 'react-loading-skeleton'
import { useTheme } from "styled-components";
import { useNavigate, useParams, Link } from "react-router-dom";
import Dialog from '../../../../../../components/dialog';
import UniversalModal from '../../../../../../components/modal-alerts/universal-modal';
import { handleStatusName } from '../../../../../../utils/handle-status-name';
import { StatusText } from '../../../../../billing/service-page/styles';
import ServiceDeleteModal from '../../../../../billing/service-page/modals/service-delete-modal';
import ServiceEnableBillingModal from '../../../../../billing/service-page/modals/service-enable-billing-modal';
import ServiceChangeProjectModal from '../../../../../billing/service-page/modals/service-change-project-modal';
import CreateServiceModal from './create-service-modal';

type ServiceModal = 'suspend' | 'resume' | 'activate' | 'terminate' | 'issue_invoice' | 'delete' | 'enable_billing' | 'change_project';

const Services:FC = () => {
  const theme:any = useTheme()
  const navigate = useNavigate()
  const { userId } = useParams();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [page] = useQueryParam('page', NumberParam);
  const [ordering] = useQueryParam('ordering', StringParam);
  const [pageSize] = useQueryParam('page_size', NumberParam);
  const [searchString] = useQueryParam('search', StringParam);
  const [services, setServices] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [servicesPageData, setServicesPageData] = useState<any[]>([])
  const [t] = useTranslation()
  const [selectedService, setSelectedService] = useState<any>(null)
  const [serviceModal, setServiceModal] = useState<ServiceModal | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [projectsByService, setProjectsByService] = useState<Record<number, any>>({});
  const {viewAlert} = useUi()
  const openServiceModal = (modal:ServiceModal, service:any) => {
    setSelectedService(service)
    setServiceModal(modal)
  }
  const closeServiceModal = () => {
    setServiceModal(null)
    setSelectedService(null)
  }
  const getServices = () => {
    billingService.getServices({
      page:page || 1,
      page_size:pageSize || 10,
      ordering:ordering,
      search:searchString||'',
      client:userId,
    })
    .then((res:any) => {
      setServices(res.data.objects.map((service:any) => {
        return {
          ...service,
          name:service.product.name,
          // status:<StatusText status={service.status}>{handleStatusName(service.status, t)}</StatusText>,
          created_at:timeConverterMMDDYYHHMM(service.created_at),
          navigate:`/billing/services/${service.id.toString()}`,
          paid_until:timeConverterMMDDYYHHMM(service.paid_until),
          _serviceId: service.id,
        }
      }))
      setServicesPageData(res.data)
      // Fetch associated projects for each service
      const serviceObjs = res.data.objects || [];
      const promises = serviceObjs.map((s: any) =>
        billingService.hasAssociatedResource(s.id)
          .then((projRes: any) => {
            const data = projRes.data;
            if (data?.has_associated_resource && data?.resource_details?.project_id) {
              // Search project by project_id to get internal id
              return projectService.getProjectList({
                pageSize: 1,
                page: 1,
                search: data.resource_details.project_id,
              }).then((listRes: any) => {
                const proj = (listRes.data?.objects || [])[0];
                return { serviceId: s.id, project: proj || null };
              }).catch(() => ({ serviceId: s.id, project: null }));
            }
            return { serviceId: s.id, project: null };
          })
          .catch(() => ({ serviceId: s.id, project: null }))
      );
      Promise.all(promises).then((results) => {
        const mapping: Record<number, any> = {};
        results.forEach(({ serviceId, project }: any) => {
          if (project?.id) {
            mapping[serviceId] = project;
          }
        });
        setProjectsByService(mapping);
      });
      setPageLoading(false)
    })
    .catch((e:any) => {
      console.log('error', e);
    })
  }
  useEffect(() => {
    getServices()
    const interval = setInterval(() => {
      getServices()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page,
    ordering,
    pageSize,
    searchString])
  const executeServiceAction = async (
    request:() => Promise<any>,
    successMessage:string,
    errorMessage:string,
  ) => {
    setActionLoading(true)
    try {
      const res = await request()
      viewAlert({
        severity: 'success',
        message: res.data?.detail || successMessage,
      })
      closeServiceModal()
      getServices()
    } catch (err:any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || errorMessage,
      })
    } finally {
      setActionLoading(false)
    }
  }

  const handleSuspend = () => {
    if (!selectedService) return
    executeServiceAction(
      () => billingService.suspendService(selectedService.id),
      t('SERVICE_SUSPENDED_SUCCESSFULLY'),
      t('FAILED_TO_SUSPEND_SERVICE'),
    )
  }

  const handleResume = () => {
    if (!selectedService) return
    executeServiceAction(
      () => billingService.resumeService(selectedService.id),
      t('SERVICE_RESUMED_SUCCESSFULLY'),
      t('FAILED_TO_RESUME_SERVICE'),
    )
  }

  const handleActivate = () => {
    if (!selectedService) return
    executeServiceAction(
      () => billingService.activateService(selectedService.id),
      t('SERVICE_ACTIVATED_SUCCESSFULLY'),
      t('FAILED_TO_ACTIVATE_SERVICE'),
    )
  }

  const handleTerminate = () => {
    if (!selectedService) return
    executeServiceAction(
      () => billingService.terminateService(selectedService.id),
      t('SERVICE_TERMINATED_SUCCESSFULLY'),
      t('FAILED_TO_TERMINATE_SERVICE'),
    )
  }

  const handleIssueInvoice = () => {
    if (!selectedService) return
    executeServiceAction(
      () => billingService.issueInvoice(selectedService.id),
      t('INVOICE_ISSUED_SUCCESSFULLY'),
      t('FAILED_TO_ISSUE_INVOICE'),
    )
  }

  const handleDelete = async (deleteOnlyFromDb:boolean, deleteAssociatedResources:boolean) => {
    if (!selectedService) return
    await executeServiceAction(
      () => billingService.deleteService(selectedService.id, {
        delete_only_from_db:deleteOnlyFromDb,
        delete_associated_resources:deleteAssociatedResources,
      }),
      t('SERVICE_DELETED_SUCCESSFULLY'),
      t('FAILED_TO_DELETE_SERVICE'),
    )
  }

  const handleEnableBilling = async (newCycleStart:string) => {
    if (!selectedService) return
    await executeServiceAction(
      () => billingService.enableBilling(selectedService.id, newCycleStart),
      t('BILLING_ENABLED_SUCCESSFULLY'),
      t('FAILED_TO_ENABLE_BILLING'),
    )
  }

  const handleChangeProject = async (projectId:string) => {
    if (!selectedService) return
    await executeServiceAction(
      () => billingService.changeResourceAssociation(selectedService.id, projectId),
      t('PROJECT_CHANGED_SUCCESSFULLY'),
      t('FAILED_TO_CHANGE_PROJECT'),
    )
  }

  const handleCustomAction = async (service:any, actionKey:string, actionDefinition:any) => {
    setActionLoading(true)
    try {
      const res = await billingService.executeAction(service.id, { action:actionKey })
      viewAlert({
        severity: 'success',
        message: res.data?.detail || `${actionDefinition.display_name} executed successfully`,
      })
      getServices()
    } catch (err:any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || `Failed to execute ${actionDefinition.display_name}`,
      })
    } finally {
      setActionLoading(false)
    }
  }

  const buildServiceMenuItems = (service:any) => {
    const menuItems:any[] = [{
      title:t('EDIT'),
      id:'edit',
      icon:icons.Edit,
      divider:false,
      onClick:() => navigate(`/billing/services/${service.id}/edit`),
    }]

    if (service.status === 'active') {
      menuItems.push({
        title:t('SUSPEND_SERVICE'),
        id:'suspend',
        icon:icons.PauseIcon,
        divider:false,
        onClick:() => openServiceModal('suspend', service),
      })
    } else if (service.status === 'suspended') {
      menuItems.push({
        title:t('RESUME_SERVICE'),
        id:'resume',
        icon:icons.PlayIcon,
        divider:false,
        onClick:() => openServiceModal('resume', service),
      })
    } else if (['pending', 'terminated'].includes(service.status)) {
      menuItems.push({
        title:t('ACTIVATE_SERVICE'),
        id:'activate',
        icon:icons.PlusDefault,
        divider:false,
        onClick:() => openServiceModal('activate', service),
      })
    }

    if (['payment canceled', 'refunded'].includes(service.last_service_cycle_state)) {
      menuItems.push({
        title:t('ENABLE_BILLING'),
        id:'enable_billing',
        icon:icons.PlayIcon,
        divider:false,
        onClick:() => openServiceModal('enable_billing', service),
      })
    }

    if (service.status !== 'terminated') {
      menuItems.push({
        title:t('TERMINATE_SERVICE'),
        id:'terminate',
        icon:icons.CloseIcon,
        divider:false,
        onClick:() => openServiceModal('terminate', service),
      })
    }

    if (['active', 'suspended'].includes(service.status)) {
      menuItems.push({
        title:t('ISSUE_INVOICE'),
        id:'issue_invoice',
        icon:icons.Document,
        divider:false,
        onClick:() => openServiceModal('issue_invoice', service),
      })
    }

    if (service.product?.product_type === 'openstack') {
      menuItems.push({
        title:t('CHANGE_PROJECT'),
        id:'change_project',
        icon:icons.Settings,
        divider:false,
        onClick:() => openServiceModal('change_project', service),
      })
    }

    if (service.actions && typeof service.actions === 'object') {
      Object.keys(service.actions).forEach((actionKey) => {
        const actionDefinition = service.actions[actionKey]
        menuItems.push({
          title:actionDefinition.display_name,
          id:`action_${actionKey}`,
          icon:icons.Power,
          divider:false,
          onClick:() => handleCustomAction(service, actionKey, actionDefinition),
        })
      })
    }

    menuItems.push({
      title:t('UPGRADE_DOWNGRADE'),
      id:'upgrade',
      icon:icons.Resize,
      divider:true,
      disabled:!['pending', 'active'].includes(service.status),
      onClick:() => navigate(`/billing/services/${service.id}/upgrade`),
    })
    menuItems.push({
      title:t('DELETE_SERVICE'),
      id:'delete',
      icon:icons.DeleteIcon,
      divider:false,
      onClick:() => openServiceModal('delete', service),
    })

    return menuItems
  }

const ColumnsServices = [
  {
    title:t("NAME"),
    key:'name'
  },
  {
    title:t("PROJECT"),
    key:'project_link'
  },
  {
    title:t("STATUS"),
    key:'status'
  },
  {
    title:t("PAID_UNTIL"),
    key:'paid_until'
  },
  {
    title:t("CREATED_AT"),
    key:'created_at'
  },
  {
    title:'',
    key:'props',
  }
]

  const servicesWithProjects = services.map((service:any) => {
    const project = projectsByService[service._serviceId]

    return {
      ...service,
      status:(
        <StatusText status={service.status}>
          {handleStatusName(service.status, t)}
        </StatusText>
      ),
      project_link:project ? (
        <Link to={`/openstack/projects/${project.id}`} style={{ textDecoration:'underline' }}>
          {project.name}
        </Link>
      ) : '—',
      dropOptions:buildServiceMenuItems(service),
    }
  })

  const selectedServiceName = selectedService?.display_name || selectedService?.product?.name
  const universalModalConfigs:Record<string, any> = {
    suspend:{
      title:t('SUSPEND_SERVICE'),
      subtitle:t('SUSPEND_SERVICE_CONFIRM', { name:selectedServiceName }),
      action:handleSuspend,
      actionTitle:t('SUSPEND_SERVICE'),
    },
    resume:{
      title:t('RESUME_SERVICE'),
      subtitle:t('RESUME_SERVICE_CONFIRM', { name:selectedServiceName }),
      action:handleResume,
      actionTitle:t('RESUME_SERVICE'),
    },
    activate:{
      title:t('ACTIVATE_SERVICE'),
      subtitle:t('ACTIVATE_SERVICE_CONFIRM', { name:selectedServiceName }),
      action:handleActivate,
      actionTitle:t('ACTIVATE'),
    },
    terminate:{
      title:t('TERMINATE_SERVICE'),
      subtitle:t('TERMINATE_SERVICE_CONFIRM', { name:selectedServiceName }),
      action:handleTerminate,
      actionTitle:t('TERMINATE_SERVICE'),
    },
    issue_invoice:{
      title:t('ISSUE_INVOICE'),
      subtitle:t('ISSUE_INVOICE_CONFIRM', { name:selectedServiceName }),
      action:handleIssueInvoice,
      actionTitle:t('ISSUE_INVOICE'),
    },
  }
  const universalModalConfig = serviceModal ? universalModalConfigs[serviceModal] : null

  if(pageLoading) return (
    <Container>
      <TopProgress loading={pageLoading}/>
      <Skeleton
        baseColor={theme.palette[300]}
        highlightColor={theme.palette[20]}
        width={125} style={{marginBottom:"5px"}} height={30}
      />
      <TableSkeleton
          rows={5}
          columns={5}
          // columnsData={Columns}
        />
    </Container>
  )
  return (
    <Container>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px'}}>
        <Typography variant="h2">
          {t("SERVICES")}
        </Typography>
        <div>
          <Button
            variant='secondary'
            size='small'
            title={t("CREATE_OPENSTACK_SERVICE")}
            onClick={() => setCreateModalOpen(true)}
          />
        </div>
      </div>
      {servicesWithProjects.length === 0 ? (
        <Typography variant="body" customStyles={{opacity:'0.5'}}>
          {t("NO_SERVICES_FOR_CLIENT")}
        </Typography>
      ) : (
        <Table
          columns={ColumnsServices}
          rows={servicesWithProjects}
          hideCheckbox
          paginationData={servicesPageData}
        />
      )}
      <Dialog noIframe open={createModalOpen} handleClose={() => setCreateModalOpen(false)}>
        <CreateServiceModal
          clientId={userId!}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false)
            getServices()
          }}
        />
      </Dialog>
      {selectedService && universalModalConfig ? (
        <Dialog noIframe open handleClose={closeServiceModal}>
          <UniversalModal
            alertTitle={universalModalConfig.title}
            alertSubTitle={universalModalConfig.subtitle}
            closeModal={universalModalConfig.action}
            closeModalCancel={closeServiceModal}
            loading={actionLoading}
            confirmButtonText={t('CANCEL')}
            cancelButtonText={universalModalConfig.actionTitle}
          />
        </Dialog>
      ) : null}
      {selectedService && serviceModal === 'delete' ? (
        <Dialog noIframe open handleClose={closeServiceModal}>
          <ServiceDeleteModal
            service={selectedService}
            onDelete={handleDelete}
            onCancel={closeServiceModal}
            loading={actionLoading}
          />
        </Dialog>
      ) : null}
      {selectedService && serviceModal === 'enable_billing' ? (
        <Dialog noIframe open handleClose={closeServiceModal}>
          <ServiceEnableBillingModal
            service={selectedService}
            onEnable={handleEnableBilling}
            onCancel={closeServiceModal}
            loading={actionLoading}
          />
        </Dialog>
      ) : null}
      {selectedService && serviceModal === 'change_project' ? (
        <Dialog noIframe open handleClose={closeServiceModal}>
          <ServiceChangeProjectModal
            service={selectedService}
            onChangeProject={handleChangeProject}
            onCancel={closeServiceModal}
            loading={actionLoading}
          />
        </Dialog>
      ) : null}
    </Container>
  )
}

export default Services
